/**
 * RateSetsEditor — Admin > Client rates > "Standard rate sets"
 * (tenants.config billing.rate_sets, made visible and editable for the
 * seat that sets client rates).
 *
 * One row per set: a name, a stable id (derived from the name until
 * the first save, then locked, because a client-creation picker and
 * the apply endpoint submit it), and one amount per PRICEABLE band. A
 * blank amount means the set leaves that band alone: applying it
 * writes nothing there and the client stays "No rate on file" on that
 * band. The save PUTs the whole list; the server refuses a band
 * outside the vocabulary fact-shaped and writes nothing.
 *
 * A save invalidates ['tenant-config'] so the AddClientDialog picker
 * and the Rates card re-read the new list.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { FLD } from '@/components/primitives/modalFormStyles';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { ApiError } from '@/lib/api';
import { rateExample } from '@/lib/clientRates';
import { contactSentence, saveFailureText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { RateBandOption } from '@/lib/notes';
import {
  MAX_RATE_SET_NAME_LENGTH,
  RATE_SET_ID_RE,
  canonicalMoneyOrNull,
  fetchRateSets,
  putRateSets,
  rateSetsKey,
  slugifyRateSetName,
  type RateSetWire,
} from '@/lib/rateSets';

interface EditableSet {
  /** Stable render identity (never the id: a new set's id follows its
   *  name while typing). */
  uid: string;
  id: string;
  name: string;
  /** band -> the typed amount ('' = blank, the set leaves it alone). */
  amounts: Record<string, string>;
  /** true = already saved; the id is submitted by pickers, so locked. */
  locked: boolean;
}

let nextUid = 0;

function toEditable(sets: readonly RateSetWire[]): EditableSet[] {
  return sets.map((s) => ({ uid: `saved-${s.id}`, id: s.id, name: s.name, amounts: { ...s.rates }, locked: true }));
}

/** The polite pre-flight problems (the server re-validates). */
function draftProblem(sets: EditableSet[], bands: readonly RateBandOption[]): string | null {
  const seen = new Set<string>();
  for (const s of sets) {
    const name = s.name.trim();
    if (name === '') return 'Every rate set needs a name.';
    if (name.length > MAX_RATE_SET_NAME_LENGTH) return `"${name.slice(0, 20)}..." is too long. Shorten the name, then save.`;
    if (!RATE_SET_ID_RE.test(s.id)) return `"${name}" needs a short code. Use only lowercase letters, numbers and underscores, with no spaces.`;
    if (seen.has(s.id)) return `Two rate sets use the code "${s.id}". Give one of them a different code.`;
    seen.add(s.id);
    let any = false;
    for (const b of bands) {
      const raw = (s.amounts[b.value] ?? '').trim();
      if (raw === '') continue;
      if (canonicalMoneyOrNull(raw) === null) {
        return `"${name}", ${b.label}: enter the amount as dollars and cents, with no $ sign (${rateExample(b.value)}).`;
      }
      any = true;
    }
    if (!any) return `"${name}" needs at least one amount.`;
  }
  return null;
}

function toWire(sets: EditableSet[], bands: readonly RateBandOption[]): RateSetWire[] {
  return sets.map((s) => {
    const rates: Record<string, string> = {};
    for (const b of bands) {
      const canon = canonicalMoneyOrNull(s.amounts[b.value] ?? '');
      if (canon !== null) rates[b.value] = canon;
    }
    return { id: s.id, name: s.name.trim(), rates };
  });
}

function saveErrorMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return contactSentence(
        "Changing rate sets isn't part of your access. Ask {contact_billing} to make this change.",
        contact,
      );
    }
    if (err.status === 422) {
      const band = (err.body as { rate_band?: string } | null)?.rate_band;
      return band
        ? `"${band}" isn't a rate band any more. Remove that amount and save again.`
        : "One amount is for a rate band that's no longer on the Rate bands tab, so nothing was saved. Remove the amount for the band that isn't listed there, then save.";
    }
    if (err.status === 400) return "These rate sets didn't save. Check each name and amount, then save again.";
  }
  return (
    saveFailureText(err, contact) ??
    contactSentence("That didn't save. Try again, and if it happens again, tell {contact_system}.", contact)
  );
}

export interface RateSetsEditorProps {
  tenantSlug: string;
  /** The priceable vocabulary (the grid's columns) — one amount each. */
  bands: readonly RateBandOption[];
}

export function RateSetsEditor({ tenantSlug, bands }: RateSetsEditorProps): ReactNode {
  const queryClient = useQueryClient();
  const query = useQuery<RateSetWire[], Error>({
    queryKey: rateSetsKey(tenantSlug),
    queryFn: ({ signal }) => fetchRateSets(tenantSlug, signal),
    staleTime: 30_000,
  });

  // The draft, seeded from the fetched list on first arrival (the
  // RateBandsEditorTab pattern): null until the query answers.
  const [draft, setDraft] = useState<EditableSet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();
  const sets = draft ?? (query.data ? toEditable(query.data) : null);

  const saveMut = useMutation<RateSetWire[], unknown, RateSetWire[]>({
    mutationFn: (wire) => putRateSets(tenantSlug, wire),
    onSuccess: (saved) => {
      setError(null);
      setDraft(toEditable(saved));
      queryClient.setQueryData(rateSetsKey(tenantSlug), saved);
      void queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantSlug] });
    },
    onError: (err) => setError(saveErrorMessage(err, contact)),
  });

  function update(uid: string, patch: (s: EditableSet) => EditableSet): void {
    setError(null);
    setDraft((sets ?? []).map((s) => (s.uid === uid ? patch(s) : s)));
  }

  const problem = sets === null ? null : draftProblem(sets, bands);
  const dirty = draft !== null;
  const canSave = sets !== null && problem === null && dirty && !saveMut.isPending;

  return (
    <section className="mt-8" data-testid="rate-sets-editor">
      <h3 className="font-display text-base text-sage-800">Standard rate sets</h3>
      <p className="mt-1 text-sm text-ink-soft">
        A named set of rates to give a new client in one step, from the Add client form or from a
        client&rsquo;s Rates card. Leave a kind of work blank and the set leaves it unset.
      </p>

      {query.isError && (
        <div className="mt-3">
          <ErrorBanner error={query.error} onRetry={() => void query.refetch()} what="the rate sets" />
        </div>
      )}
      {query.isPending && (
        <div className="mt-3">
          <SkeletonRow />
        </div>
      )}

      {sets !== null && (
        <div className="mt-3 overflow-x-auto">
          {sets.length === 0 && (
            <p className="text-sm text-ink-soft" data-testid="rate-sets-empty">
              Add a rate set to pre-fill a new client's rates.
            </p>
          )}
          {sets.length > 0 && (
            <table className="w-full text-sm" data-testid="rate-sets-table">
              <thead>
                <tr className="text-left text-label font-semibold uppercase tracking-[0.12em] text-sage-400">
                  <th className="py-1.5 pr-3 font-semibold">Name</th>
                  {bands.map((b) => (
                    <th key={b.value} className="py-1.5 px-2 font-semibold whitespace-nowrap">
                      {b.label}
                    </th>
                  ))}
                  <th className="py-1.5 pl-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-sage-800/[6%]">
                {sets.map((s, i) => (
                  <tr key={s.uid} data-testid={`rate-sets-row-${i}`}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        aria-label="Rate set name"
                        data-testid={`rate-sets-name-${i}`}
                        className={`${FLD} h-9 min-w-[10rem] px-2 text-title-sm`}
                        value={s.name}
                        onChange={(e) =>
                          update(s.uid, (cur) => ({
                            ...cur,
                            name: e.target.value,
                            id: cur.locked ? cur.id : slugifyRateSetName(e.target.value),
                          }))
                        }
                      />
                      <code className="mt-1 block text-label text-ink-soft" data-testid={`rate-sets-id-${i}`}>
                        {s.id}
                      </code>
                    </td>
                    {bands.map((b) => (
                      <td key={b.value} className="py-2 px-2 align-top">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`${s.name || 'Rate set'}: ${b.label} rate`}
                          placeholder="blank"
                          data-testid={`rate-sets-amount-${i}-${b.value}`}
                          className={`${FLD} h-9 w-24 px-2 text-title-sm`}
                          value={s.amounts[b.value] ?? ''}
                          onChange={(e) =>
                            update(s.uid, (cur) => ({
                              ...cur,
                              amounts: { ...cur.amounts, [b.value]: e.target.value },
                            }))
                          }
                        />
                      </td>
                    ))}
                    <td className="py-2 pl-2 align-top">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`rate-sets-remove-${i}`}
                        onClick={() => {
                          setError(null);
                          setDraft((sets ?? []).filter((x) => x.uid !== s.uid));
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              data-testid="rate-sets-add"
              onClick={() => {
                setError(null);
                nextUid += 1;
                setDraft([...(sets ?? []), { uid: `new-${nextUid}`, id: '', name: '', amounts: {}, locked: false }]);
              }}
            >
              Add rate set
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!canSave}
              loading={saveMut.isPending}
              data-testid="rate-sets-save"
              onClick={() => {
                if (sets !== null) saveMut.mutate(toWire(sets, bands));
              }}
            >
              Save rate sets
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="rate-sets-discard"
                onClick={() => {
                  setError(null);
                  setDraft(null);
                }}
              >
                Discard changes
              </Button>
            )}
          </div>
          {problem !== null && dirty && (
            <p className="mt-2 text-xs text-pepper" data-testid="rate-sets-problem">
              {problem}
            </p>
          )}
          {error !== null && (
            <p role="alert" className="mt-2 text-xs text-pepper" data-testid="rate-sets-error">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
